import axios, { AxiosResponse } from "axios";
import { BASE_URL } from "@/store/urls";

const ML_GATEWAY_BASE_URL = (
  process.env.VUE_APP_ML_GATEWAY_URL || "https://ml-stag.ibhc.ai"
).replace(/\/$/, "");
const ML_INTERVIEW_AGENT_BASE_URL = `${ML_GATEWAY_BASE_URL}/interview-agent`;
const ML_GATEWAY_WS_ORIGIN = ML_GATEWAY_BASE_URL.replace(
  /^https:/,
  "wss:"
).replace(/^http:/, "ws:");
const BACKEND_RECRUITER_BASE_URL = `${BASE_URL}recruiter`;
export const ML_INTERVIEW_WS_URL = `${ML_GATEWAY_WS_ORIGIN}/interview-agent/interview-chat`;

export interface WsTranscriptMessage {
  role: "assistant" | "user";
  content: string;
}

export interface InterviewTranscriptMessage {
  speaker: "interviewer" | "candidate";
  text: string;
}

export const generate_ai_report = async (payload: {
  resume_text: string;
  jd_text: string;
  transcript: Array<Record<string, unknown>>;
  question_bank?: unknown[];
}): Promise<AxiosResponse> => {
  return axios.post(
    `${BACKEND_RECRUITER_BASE_URL}/interview/ml/generate-ai-report`,
    payload
  );
};

export const generate_questions = async (payload: {
  entity: string;
  entity_id: string;
  user_type: "recruiter";
  user_id: string;
  resume_text: string;
  jd_text: string;
  pre_interview_form?: Record<string, unknown>;
  interviewer_email?: string;
}): Promise<AxiosResponse> => {
  return axios.post(
    `${BACKEND_RECRUITER_BASE_URL}/interview/ml/generate-questions`,
    payload
  );
};

export const generate_guidelines = async (payload: {
  ai_report: Record<string, unknown>;
  question_bank: unknown[];
  resume_text: string;
  asked_question_ids?: string[];
}): Promise<AxiosResponse> => {
  return axios.post(
    `${BACKEND_RECRUITER_BASE_URL}/interview/ml/generate-guidelines`,
    payload
  );
};

export const generate_audit = async (payload: {
  interviewer_brief: Record<string, unknown>;
  human_transcript: unknown[];
  ai_report: Record<string, unknown>;
}): Promise<AxiosResponse> => {
  return axios.post(
    `${BACKEND_RECRUITER_BASE_URL}/interview/ml/generate-audit`,
    payload
  );
};

export const generate_final_report = async (payload: {
  resume_text: string;
  jd_text: string;
  pre_interview_form?: Record<string, unknown>;
  ai_report: Record<string, unknown>;
  audit_report: Record<string, unknown>;
  ai_transcript: WsTranscriptMessage[];
  human_transcript: unknown[];
}): Promise<AxiosResponse> => {
  return axios.post(
    `${BACKEND_RECRUITER_BASE_URL}/interview/ml/generate-final-report`,
    payload
  );
};

export const upsert_interview_report = async (payload: {
  candidate_id: number;
  interview_type: "ai" | "person";
  job_id?: number | null;
  thread_id?: string | null;
  ai_transcript?: InterviewTranscriptMessage[] | WsTranscriptMessage[] | null;
  human_transcript?: unknown[] | null;
  question_bank?: unknown[] | null;
  ai_report?: Record<string, unknown> | null;
  interviewer_brief?: Record<string, unknown> | null;
  audit_report?: Record<string, unknown> | null;
  final_report?: Record<string, unknown> | null;
  status?: string | null;
}): Promise<AxiosResponse> => {
  return axios.post(`${BACKEND_RECRUITER_BASE_URL}/interview/ml/report`, payload);
};

export const get_latest_interview_report = async (
  candidateId: number,
  interviewType: "ai" | "person" = "ai",
  jobId?: number | null
): Promise<AxiosResponse> => {
  const jobQuery =
    jobId !== undefined && jobId !== null && Number.isFinite(Number(jobId))
      ? `&job_id=${Number(jobId)}`
      : "";
  return axios.get(
    `${BACKEND_RECRUITER_BASE_URL}/interview/ml/report/${candidateId}?interview_type=${interviewType}${jobQuery}`
  );
};

export const upsert_interview_invite = async (payload: {
  interview_type: "ai" | "person";
  candidate_id: number;
  job_id: number | null;
  status?: string;
  scheduled_at?: string;
  [key: string]: unknown;
}): Promise<AxiosResponse> => {
  return axios.post(`${BACKEND_RECRUITER_BASE_URL}/interview/ml/invite`, payload);
};

export const get_interview_invites = async (
  interviewType: "ai" | "person"
): Promise<AxiosResponse> => {
  return axios.get(
    `${BACKEND_RECRUITER_BASE_URL}/interview/ml/invites?interview_type=${interviewType}`
  );
};

export const get_session_result = async (
  threadId: string,
  userId: string
): Promise<AxiosResponse> => {
  return axios.get(`${ML_INTERVIEW_AGENT_BASE_URL}/sessions/${threadId}/result`, {
    headers: { "X-User-Id": userId }
  });
};
